import { API_ROOT } from '../config';

/**
 * The one place a request to this app's API is made.
 *
 * Wraps the 53 bare `fetch` calls the vanilla app spreads across its script. Three things
 * they each re-implement, or forget to:
 *
 * - `credentials: 'same-origin'` — the session is an httpOnly cookie, so a request without
 *   it is anonymous. It is the default for same-origin requests, stated here so nobody
 *   "helpfully" adds a mode that changes it.
 * - a non-2xx is an ERROR. `fetch` resolves for a 500, and a caller that forgets to check
 *   `res.ok` parses an error page as data.
 * - `503 feature-disabled` is not a failure of the request, it is the deployment saying it
 *   does not offer that feature (see DEPLOYMENTS.md). Callers want to tell the two apart.
 *
 * AND ONE THING NO CALLER SHOULD HAVE TO HANDLE: a 401. Every caller in this app assumes a
 * session, so before V0.45 a lost one produced whatever each of them does with an error —
 * mostly an empty panel and a console line. The observed symptom was a blank screen with no
 * explanation, which `ApiError.isUnauthorised` existed to prevent and nobody consulted.
 * `configureUnauthorised()` gives that answer ONE home: any 401, from any route, tells the
 * app the session is gone. See `onSessionLost` in app/boot.ts for what happens next, and for
 * why /api/auth/* is excluded there rather than here (a wrong password is a legitimate 401).
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The deployment does not offer this feature. Hide the surface; do not report a fault. */
  get isFeatureDisabled(): boolean {
    return this.status === 503 && (this.body as { error?: string } | undefined)?.error === 'feature-disabled';
  }

  /** Signed out, or the session expired. */
  get isUnauthorised(): boolean {
    return this.status === 401;
  }

  /**
   * The server is temporarily refusing everything because an administrator is restoring a
   * backup (V0.48). Not a fault, and NOT `feature-disabled`: that one means "this deployment
   * does not have this feature" and the answer to it is to hide a control permanently. This
   * one means "ask again in a moment" and the answer is to say so and keep the app on screen.
   * The two share a status code and nothing else, which is exactly why the server sends a
   * different `error` string rather than reusing the one the client already knows.
   */
  get isMaintenance(): boolean {
    return this.status === 503 && (this.body as { error?: string } | undefined)?.error === 'maintenance';
  }
}

/**
 * Told whenever a request comes back 401. Set once, at boot.
 *
 * A sink rather than a thrown-and-caught signal: the 401 has to be reported even when the
 * caller swallows its error, which several deliberately do (a poll, a best-effort fetch).
 */
type UnauthorisedSink = (url: string) => void;
let onUnauthorised: UnauthorisedSink | null = null;

export function configureUnauthorised(fn: UnauthorisedSink | null): void {
  onUnauthorised = fn;
}

/**
 * Told whenever a request comes back "the server is restoring a backup" (V0.48).
 *
 * A sink for the same reason the 401 one is: the fact has to reach the app even when the
 * caller swallows its error, and during a restore EVERY route answers this way — a popup
 * save, a search, a version poll. Without one home for it, a restore would look like a
 * scatter of unrelated failures, each handled by whatever that call site does with an error,
 * which is mostly nothing.
 *
 * Deliberately NOT fired from `apiFetchRaw`: reading its body to tell maintenance from any
 * other 503 would consume the stream its two callers are about to read themselves. Both are
 * uploads that report the server's own message, so the loss is a banner they would have
 * raised a second before the next request raises it anyway.
 */
type MaintenanceSink = (url: string) => void;
let onMaintenance: MaintenanceSink | null = null;

export function configureMaintenance(fn: MaintenanceSink | null): void {
  onMaintenance = fn;
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  /**
   * Let the request outlive the page. Passed straight to `fetch`, and only meaningful for a
   * small body — the browser caps it. Nothing sets it yet; it is here so a `pagehide` flush
   * has somewhere to go without every call site learning about `fetch` again.
   */
  keepalive?: boolean;
  /** Extra query parameters. Undefined and null values are dropped rather than stringified. */
  query?: Record<string, string | number | boolean | null | undefined>;
  /**
   * Cache mode. Only `GET /api/map` sets it, to `no-store`: the map is the whole application
   * state and a browser serving a cached copy after a reload is how somebody ends up editing
   * a map that has already moved on.
   */
  cache?: RequestCache;
}

function withQuery(path: string, query: RequestOptions['query']): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${path}${path.includes('?') ? '&' : '?'}${qs}` : path;
}

/**
 * A raw `fetch` that still reports a 401.
 *
 * For the two multipart uploads — a document image and a user's avatar — whose bodies cannot
 * go through `apiRequest` at all: setting a JSON content type strips the boundary the server
 * needs to parse the form. They kept their own `fetch` calls, which meant they were the two
 * routes in the app where an expired session produced only "Upload failed".
 *
 * Everything else about them is unchanged: the caller still reads the Response itself, so its
 * own error wording and its own JSON handling stay exactly where they were.
 */
export async function apiFetchRaw(url: string, init: RequestInit = {}): Promise<Response> {
  const res = await fetch(url, { credentials: 'same-origin', ...init });
  if (res.status === 401) onUnauthorised?.(url);
  return res;
}

export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = withQuery(path.startsWith('/') ? path : `${API_ROOT}/${path}`, opts.query);

  const res = await fetch(url, {
    method: opts.method ?? 'GET',
    credentials: 'same-origin',
    headers: opts.body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
    cache: opts.cache,
    keepalive: opts.keepalive,
  });

  if (!res.ok) {
    // Strictly 401. A 403 is "you may not do THAT" and a 409 is "the map moved" — both are
    // answers to a live session, and treating either as a lost one would sign the user out
    // of a working app (e2e/43-concurrency pins that the login card stays away after a 409).
    if (res.status === 401) onUnauthorised?.(url);
    // Every /api/* route answers JSON, including its errors — but a proxy or a crash can
    // still produce HTML, so parsing must not throw over the top of the real status.
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = undefined;
    }
    const detail = (body as { error?: string } | undefined)?.error;
    // Strictly the maintenance 503, never the `feature-disabled` one: an air-gapped
    // deployment answering that for every AI route must not put a "back in a moment" banner
    // on screen for a feature that is never coming back.
    if (res.status === 503 && detail === 'maintenance') onMaintenance?.(url);
    throw new ApiError(`${opts.method ?? 'GET'} ${url} failed: ${res.status}${detail ? ` (${detail})` : ''}`, res.status, body);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
